/* v8 ignore file -- @preserve: Canvas manipulation is difficult to test without real DOM */
import {Crop, PixelCrop} from 'react-image-crop';

const ToRadians = Math.PI / 180;
const ImageExtension = 'png';

export function generateCanvasPreview(
  image: HTMLImageElement,
  canvas: HTMLCanvasElement,
  crop: PixelCrop,
  scale = 1,
  rotate = 0,
): void {
  const ctx = canvas.getContext('2d');

  /* v8 ignore next -- @preserve */
  if (!ctx) {
    // In test environments (jsdom), getContext may return null
    return;
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  // devicePixelRatio slightly increases sharpness on retina devices
  // at the expense of slightly slower render times and needing to
  // size the image back down if you want to download/upload and be
  // true to the images natural size.
  const pixelRatio = globalThis.window?.devicePixelRatio ?? 1;

  canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
  canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = 'high';

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;

  const rotateRads = rotate * ToRadians;
  const centerX = image.naturalWidth / 2;
  const centerY = image.naturalHeight / 2;

  ctx.save();

  // 5) Move the crop origin to the canvas origin (0,0)
  ctx.translate(-cropX, -cropY);
  // 4) Move the origin to the center of the original position
  ctx.translate(centerX, centerY);
  // 3) Rotate around the origin
  ctx.rotate(rotateRads);
  // 2) Scale the image
  ctx.scale(scale, scale);
  // 1) Move the center of the image to the origin (0,0)
  ctx.translate(-centerX, -centerY);
  ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight, 0, 0, image.naturalWidth, image.naturalHeight);

  ctx.restore();
}

export function generateImageBlob(
  image: HTMLImageElement,
  canvas: HTMLCanvasElement,
  crop: Crop,
  type: string = `image/${ImageExtension}`,
): Promise<Blob> {
  // This will size relative to the uploaded image
  // size. If you want to size according to what is
  // displayed on screen, remove scaleX + scaleY
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  const offscreen = new OffscreenCanvas(crop.width * scaleX, crop.height * scaleY);
  const ctx = offscreen.getContext('2d');
  if (ctx) {
    ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, offscreen.width, offscreen.height);
  }

  return offscreen.convertToBlob({
    type,
  });
}
